import {createTheme, Theme, ThemeOptions} from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    panel: true;
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    soft: true;
  }
}

// The admin look in one place. Admin screens read these through the theme; sx may import them for one-offs.
export const adminTokens = {
  ink: '#20252c',
  muted: '#69717d',
  line: '#e0e3e8',
  lineSoft: '#edf0f3',
  canvas: '#f7f8fa',
  accent: '#08796f',
  accentDark: '#065d55',
  warnText: '#a65b2e',
} as const;

const softTints = {
  success: ['#e7f4ed', '#1c7154'],
  warning: ['#fff0e1', '#956020'],
  error: ['#fbeeea', '#a5493c'],
  info: ['#eceef7', '#5b5f7a'],
} as const;

export function createAdminTheme(outer: Theme): Theme {
  const t = adminTokens;
  const overrides: ThemeOptions = {
    palette: {
      primary: outer.palette.augmentColor({color: {main: t.accent, dark: t.accentDark}}),
      text: {primary: t.ink, secondary: t.muted},
      divider: t.line,
      background: {default: t.canvas},
    },
    typography: {
      h2: {fontSize: '1rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: 0},
      h3: {fontSize: '0.95rem', fontWeight: 600, lineHeight: 1.5, letterSpacing: 0},
    },
    components: {
      MuiSelect: {
        defaultProps: {
          displayEmpty: true,
          MenuProps: {
            PaperProps: {sx: {maxHeight: 320, maxWidth: 'calc(100vw - 32px)'}},
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {root: {fontSize: '0.875rem', minHeight: 40, whiteSpace: 'normal', overflowWrap: 'anywhere'}},
      },
      MuiButton: {defaultProps: {disableElevation: true}, styleOverrides: {root: {textTransform: 'none', borderRadius: 7}}},
      MuiTab: {styleOverrides: {root: {textTransform: 'none', fontWeight: 400, '&.Mui-selected': {color: t.ink, fontWeight: 600}}}},
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {gap: 3, padding: 3, borderRadius: 7, backgroundColor: '#f0f2f5'},
          grouped: {border: 0, borderRadius: 5, margin: 0},
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            border: 0,
            padding: '8px 11px',
            lineHeight: 1.25,
            color: t.ink,
            fontSize: '0.875rem',
            fontWeight: 400,
            '&.Mui-selected, &.Mui-selected:hover': {
              backgroundColor: '#fff',
              color: t.ink,
              fontWeight: 600,
              boxShadow: '0 1px 4px #1b30451c',
            },
          },
        },
      },
      MuiPaper: {
        variants: [{props: {variant: 'panel'}, style: {border: `1px solid ${t.line}`, borderRadius: 10, overflow: 'hidden'}}],
      },
      MuiChip: {
        variants: [
          {
            props: {variant: 'soft'},
            style: {
              height: 'auto',
              borderRadius: 5,
              fontSize: '0.75rem',
              fontWeight: 500,
              backgroundColor: '#eef1f5',
              color: '#606977',
              '& .MuiChip-label': {padding: '4px 8px'},
            },
          },
          {props: {variant: 'soft', size: 'small'}, style: {fontSize: '0.7rem', borderRadius: 3, '& .MuiChip-label': {padding: '2px 6px'}}},
          ...(Object.keys(softTints) as (keyof typeof softTints)[]).map(color => ({
            props: {variant: 'soft' as const, color},
            style: {backgroundColor: softTints[color][0], color: softTints[color][1]},
          })),
        ],
      },
      MuiDialog: {
        styleOverrides: {
          root: {'& .MuiBackdrop-root': {backgroundColor: '#25303c55'}},
          paper: {maxWidth: 470, borderRadius: 12, border: '1px solid #dfe3e8', boxShadow: '0 18px 80px #15263730'},
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: ({theme}) => ({
            padding: '0 24px 24px',
            [theme.breakpoints.down(700)]: {flexWrap: 'wrap', gap: 8, '& > .MuiButton-root': {flex: '1 1 100%', margin: 0}},
          }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {borderBottomColor: t.lineSoft, fontSize: '0.875rem'},
          head: {fontSize: '0.75rem', fontWeight: 500, color: t.muted, backgroundColor: '#fafbfc'},
          sizeMedium: ({theme}) => ({padding: '14px 22px', [theme.breakpoints.down(430)]: {padding: '10px 12px'}}),
        },
      },
    },
  };
  return createTheme(outer, overrides);
}
